import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import { useT } from "../i18n";
import { Select } from "./Select";

interface AudioTrackOption {
  id: number;
  name: string;
}

/**
 * Minimal typing for the (non-standard) HTMLMediaElement.audioTracks API used
 * by Safari/iOS native HLS. Cast `video` to this to access alternate tracks.
 */
interface NativeAudioTrack {
  enabled: boolean;
  language?: string;
  label?: string;
}
interface NativeAudioTrackList {
  length: number;
  [index: number]: NativeAudioTrack;
}
interface MediaElementWithAudioTracks extends HTMLVideoElement {
  audioTracks?: NativeAudioTrackList;
}

export function Player({ manifestUrl }: { manifestUrl: string }) {
  const { t } = useT();
  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const [audioTracks, setAudioTracks] = useState<AudioTrackOption[]>([]);
  const [activeAudio, setActiveAudio] = useState<number>(-1);

  useEffect(() => {
    const video = videoRef.current as MediaElementWithAudioTracks | null;
    if (!video) return;

    // Prefer hls.js when available (Chrome, Firefox, Edge). hls.js properly
    // handles separate audio rendition groups, which native HLS in non-Safari
    // browsers does not. Chrome reports canPlayType("application/vnd.apple.mpegurl")
    // as "maybe" (truthy) even though it cannot actually play HLS natively, so
    // checking canPlayType first would take the wrong path.
    if (Hls.isSupported()) {
      const hls = new Hls({ enableWorker: true });
      hlsRef.current = hls;
      hls.loadSource(manifestUrl);
      hls.attachMedia(video);

      hls.on(Hls.Events.AUDIO_TRACKS_UPDATED, () => {
        const tracks = hls.audioTracks;
        if (tracks.length <= 1) {
          setAudioTracks([]);
          return;
        }
        setAudioTracks(tracks.map((t) => ({ id: t.id, name: t.name || t.lang || `Audio ${t.id + 1}` })));
        setActiveAudio(hls.audioTrack);
      });

      return () => {
        hls.destroy();
        hlsRef.current = null;
      };
    }

    // Native HLS support (Safari, iOS): use the HTMLMediaElement audioTracks API.
    // Only reached when hls.js is not supported (Safari native HLS).
    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = manifestUrl;
      const onLoaded = () => {
        const list = video.audioTracks;
        if (!list || list.length <= 1) return;
        const opts: AudioTrackOption[] = [];
        for (let i = 0; i < list.length; i++) {
          const t = list[i];
          opts.push({ id: i, name: t.label || t.language || `Audio ${i + 1}` });
        }
        setAudioTracks(opts);
        const def = Array.from({ length: list.length }, (_, i) => list[i]).findIndex((t) => t.enabled);
        setActiveAudio(def >= 0 ? def : 0);
      };
      video.addEventListener("loadedmetadata", onLoaded);
      return () => {
        video.removeEventListener("loadedmetadata", onLoaded);
        video.removeAttribute("src");
        video.load();
      };
    }
  }, [manifestUrl]);

  function selectAudio(id: number) {
    setActiveAudio(id);
    if (hlsRef.current) {
      hlsRef.current.audioTrack = id;
      return;
    }
    const video = videoRef.current as MediaElementWithAudioTracks | null;
    if (video && video.canPlayType("application/vnd.apple.mpegurl")) {
      const list = video.audioTracks;
      if (list) {
        for (let i = 0; i < list.length; i++) list[i].enabled = i === id;
      }
    }
  }

  return (
    <div className="w-full aspect-video bg-black rounded-xl overflow-hidden relative">
      <video
        ref={videoRef}
        controls
        playsInline
        className="w-full h-full"
      />
      {audioTracks.length > 1 && (
        <div className="absolute top-3 right-3 z-10 flex items-center gap-2 bg-black/70 rounded-lg px-2 py-1 text-xs text-white">
          <span aria-hidden>{t("common.audio")}</span>
          <Select
            value={String(activeAudio)}
            options={audioTracks.map((t) => ({ value: String(t.id), label: t.name }))}
            onChange={(v) => selectAudio(Number(v))}
            size="sm"
            overlay
          />
        </div>
      )}
    </div>
  );
}